import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  SafeAreaView, StatusBar, Alert, RefreshControl, TextInput, Modal,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const API = 'https://api.jobeng.ai';
const KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const P = '#0e4e96';

const getH = async () => {
  const t = await AsyncStorage.getItem('auth_token').catch(() => null);
  return { 'Content-Type': 'application/json', 'x-api-key': KEY, ...(t ? { Authorization: `Bearer ${t}` } : {}) };
};

const fmt = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '--:--';
const fmtHours = (h) => h ? `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m` : '0h 0m';

export default function TimesheetScreen({ navigation, route }) {
  const empId = route?.params?.employeeId || route?.params?.employee?.id || '1';
  const [records, setRecords] = useState([]);
  const [todayRecord, setTodayRecord] = useState(null);
  const [checkedIn, setCheckedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [note, setNote] = useState('');
  const [weekTotal, setWeekTotal] = useState(0);
  const [monthTotal, setMonthTotal] = useState(0);

  const load = useCallback(async () => {
    try {
      const h = await getH();
      const res = await fetch(`${API}/api/data/attendance_records?employee_id=${parseInt(empId)||0}&limit=30`, { headers: h });
      const json = await res.json().catch(() => ({}));
      const data = Array.isArray(json?.data) ? json.data : [];
      setRecords(data);

      const today = new Date().toISOString().split('T')[0];
      const tr = data.find(r => r.attendance_date?.startsWith(today));
      setTodayRecord(tr || null);
      setCheckedIn(tr && tr.check_in && !tr.check_out);

      const weekAgo = new Date(Date.now() - 7 * 86400000);
      const weekH = data.filter(r => new Date(r.attendance_date) >= weekAgo)
        .reduce((s, r) => s + parseFloat(r.hours_worked || 0), 0);
      setWeekTotal(weekH);

      const monthAgo = new Date(Date.now() - 30 * 86400000);
      const monthH = data.filter(r => new Date(r.attendance_date) >= monthAgo)
        .reduce((s, r) => s + parseFloat(r.hours_worked || 0), 0);
      setMonthTotal(monthH);
    } catch (e) {
      console.warn('Timesheet load error:', e.message);
    } finally { setLoading(false); setRefreshing(false); }
  }, [empId]);

  useEffect(() => { load(); }, [load]);

  const checkIn = async () => {
    try {
      const h = await getH();
      const res = await fetch(`${API}/api/attendance/check-in`, {
        method: 'POST', headers: h,
        body: JSON.stringify({ employeeId: empId }),
      });
      const json = await res.json();
      if (json.success || res.ok) {
        setCheckedIn(true);
        Alert.alert('✅ Checked In', `Welcome! Checked in at ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`);
        load();
      } else Alert.alert('Error', json.error || 'Could not check in');
    } catch (e) { Alert.alert('Error', e.message); }
  };

  const checkOut = async () => {
    try {
      const h = await getH();
      const res = await fetch(`${API}/api/attendance/check-out`, {
        method: 'POST', headers: h,
        body: JSON.stringify({ employeeId: empId }),
      });
      const json = await res.json();
      if (json.success || res.ok) {
        setCheckedIn(false);
        Alert.alert('👋 Checked Out', 'Have a great rest of your day!');
        load();
      } else Alert.alert('Error', json.error || 'Could not check out');
    } catch (e) { Alert.alert('Error', e.message); }
  };

  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={s.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={s.back}>
          <MaterialCommunityIcons name="arrow-left" size={22} color="#fff" />
        </TouchableOpacity>
        <Text style={s.headerTitle}>Timesheet</Text>
        <View style={{ width: 36 }} />
      </LinearGradient>

      <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} colors={[P]} />}>
        {/* Stats */}
        <View style={s.statsRow}>
          {[
            { label: 'Today', value: todayRecord ? fmtHours(parseFloat(todayRecord.hours_worked || 0)) : checkedIn ? 'In progress' : '0h 0m', icon: 'clock-outline', color: '#10b981' },
            { label: 'This Week', value: fmtHours(weekTotal), icon: 'calendar-week', color: '#f59e0b' },
            { label: 'This Month', value: fmtHours(monthTotal), icon: 'calendar-month', color: P },
          ].map(({ label, value, icon, color }) => (
            <View key={label} style={[s.statCard, { borderTopColor: color }]}>
              <MaterialCommunityIcons name={icon} size={22} color={color} />
              <Text style={s.statVal}>{value}</Text>
              <Text style={s.statLabel}>{label}</Text>
            </View>
          ))}
        </View>

        {/* Check In/Out */}
        <View style={s.checkCard}>
          <View>
            <Text style={s.checkTitle}>{checkedIn ? 'You are checked in' : 'Not checked in'}</Text>
            {todayRecord?.check_in && <Text style={s.checkSub}>In: {fmtTime(todayRecord.check_in)}{todayRecord.check_out ? `  •  Out: ${fmtTime(todayRecord.check_out)}` : ''}</Text>}
          </View>
          <TouchableOpacity
            style={[s.checkBtn, { backgroundColor: checkedIn ? '#ef4444' : '#10b981' }]}
            onPress={checkedIn ? checkOut : checkIn}>
            <MaterialCommunityIcons name={checkedIn ? 'logout' : 'login'} size={18} color="#fff" />
            <Text style={s.checkBtnText}>{checkedIn ? 'Check Out' : 'Check In'}</Text>
          </TouchableOpacity>
        </View>

        {/* Records */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>Recent Records</Text>
          {records.length === 0 && !loading && (
            <Text style={s.empty}>No attendance records yet</Text>
          )}
          {records.slice(0, 15).map((r, i) => (
            <View key={r.id || i} style={s.recordRow}>
              <View style={[s.recordDot, { backgroundColor: r.hours_worked >= 8 ? '#10b981' : r.hours_worked > 0 ? '#f59e0b' : '#ef4444' }]} />
              <View style={{ flex: 1 }}>
                <Text style={s.recordDate}>{fmt(r.attendance_date)}</Text>
                <Text style={s.recordSub}>{fmtTime(r.check_in)} → {fmtTime(r.check_out)}</Text>
              </View>
              <Text style={s.recordHours}>{fmtHours(parseFloat(r.hours_worked || 0))}</Text>
            </View>
          ))}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14 },
  back: { padding: 4 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  statsRow: { flexDirection: 'row', gap: 10, padding: 16 },
  statCard: { flex: 1, backgroundColor: '#fff', borderRadius: 12, padding: 12, alignItems: 'center', gap: 4, borderTopWidth: 3, shadowColor: '#000', shadowOpacity: 0.06, shadowRadius: 4, elevation: 2 },
  statVal: { fontSize: 14, fontWeight: '800', color: '#1e293b' },
  statLabel: { fontSize: 10, color: '#64748b', fontWeight: '600' },
  checkCard: { marginHorizontal: 16, backgroundColor: '#fff', borderRadius: 16, padding: 18, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', shadowColor: '#000', shadowOpacity: 0.06, shadowRadius: 8, elevation: 3, marginBottom: 8 },
  checkTitle: { fontSize: 15, fontWeight: '700', color: '#1e293b' },
  checkSub: { fontSize: 12, color: '#64748b', marginTop: 2 },
  checkBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 16, paddingVertical: 10, borderRadius: 12 },
  checkBtnText: { color: '#fff', fontWeight: '700', fontSize: 13 },
  section: { margin: 16 },
  sectionTitle: { fontSize: 16, fontWeight: '800', color: '#1e293b', marginBottom: 12 },
  empty: { textAlign: 'center', color: '#94a3b8', padding: 24 },
  recordRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  recordDot: { width: 10, height: 10, borderRadius: 5 },
  recordDate: { fontSize: 13, fontWeight: '700', color: '#1e293b' },
  recordSub: { fontSize: 11, color: '#64748b', marginTop: 1 },
  recordHours: { fontSize: 13, fontWeight: '700', color: P },
});
